/* eslint-disable array-callback-return */
import _ from "lodash";
import moment from "moment";
import { store } from "..";
import {
	setFacilityPercentage as setFacilityPercentageComparison,
	setFilterTotal as setFilterTotalComparison,
	setIsCensusTotalLocked as setIsCensusTotalLockedComparison,
	setLockedByFacility as setLockedByFacilityComparison,
	setLockedTotalBy as setLockedTotalByComparison,
	setLockTotal as setLockTotalComparison,
} from "../store/reducers/comparisonReducers/overallComparison.slice";
import {
	setFacilityPercentage,
	setFilterTotal,
	setIsCensusTotalLocked,
	setLockedByFacility,
	setLockedTotalBy,
	setLockTotal,
} from "../store/reducers/overall.slice";
import { TOTAL_TYPE, EXHUAST_INSURANCE, CUSTOM_TAB_TYPE } from "../types/common.type";
import { OVERALL_CARDS_TYPE } from "../types/overall.type";
import { OVERALL_PAGE_SUB_TYPE, PAGE_TYPE } from "../types/pages.type";
import { filterCustomPatientData, getDynamicPercentageBy, itemPercentage, updateListTotalValue } from "./common";
import { batch } from "react-redux";
import { getCustomTabsCards } from "./custom-tab-filter";

// Attach level1/level2/level3/level4/level5 totals to list items for tooltip only (non-mutating calculations).
const attachLevelTotals = (list, snapshots, currentLevel, getKey, cardTitles = {}) => {
	if (!Array.isArray(list) || currentLevel < 2 || !snapshots) return list;
	const level1 = Array.isArray(snapshots.level1) ? snapshots.level1 : [];
	const level2 = Array.isArray(snapshots.level2) ? snapshots.level2 : [];
	const level3 = Array.isArray(snapshots.level3) ? snapshots.level3 : [];
	const level4 = Array.isArray(snapshots.level4) ? snapshots.level4 : [];
	const level5 = Array.isArray(snapshots.level5) ? snapshots.level5 : [];
	return list.map((item) => {
		// Preserve existing level totals if they're already set (e.g., from dxData, shiftData)
		if (item.level1Total !== undefined || item.level2Total !== undefined || item.level3Total !== undefined || item.level4Total !== undefined || item.level5Total !== undefined) {
			return {
				...item,
				...(currentLevel >= 2 && cardTitles.level1 && { level1CardTitle: cardTitles.level1 }),
				...(currentLevel >= 3 && cardTitles.level2 && { level2CardTitle: cardTitles.level2 }),
				...(currentLevel >= 4 && cardTitles.level3 && { level3CardTitle: cardTitles.level3 }),
				...(currentLevel >= 5 && cardTitles.level4 && { level4CardTitle: cardTitles.level4 }),
				...(currentLevel >= 6 && cardTitles.level5 && { level5CardTitle: cardTitles.level5 }),
			};
		}
		const key = getKey(item);
		if (key === undefined || key === null) return item;
		const level1Total = level1.filter((p) => getKey(p) === key).length;
		const level2Total = level2.filter((p) => getKey(p) === key).length;
		const level3Total = level3.filter((p) => getKey(p) === key).length;
		const level4Total = level4.filter((p) => getKey(p) === key).length;
		const level5Total = level5.filter((p) => getKey(p) === key).length;
		return {
			...item,
			...(currentLevel >= 2 && { level1Total }),
			...(currentLevel >= 3 && { level2Total }),
			...(currentLevel >= 4 && { level3Total }),
			...(currentLevel >= 5 && { level4Total }),
			...(currentLevel >= 6 && { level5Total }),
			...(currentLevel >= 2 && cardTitles.level1 && { level1CardTitle: cardTitles.level1 }),
			...(currentLevel >= 3 && cardTitles.level2 && { level2CardTitle: cardTitles.level2 }),
			...(currentLevel >= 4 && cardTitles.level3 && { level3CardTitle: cardTitles.level3 }),
			...(currentLevel >= 5 && cardTitles.level4 && { level4CardTitle: cardTitles.level4 }),
			...(currentLevel >= 6 && cardTitles.level5 && { level5CardTitle: cardTitles.level5 }),
		};
	});
};

// Helper to attach level totals when result is an array; keeps original data intact.
const maybeAttachTotals = (dataArr, snapshots, currentLevel, keySelector, cardTitles = {}) => {
	if (!Array.isArray(dataArr)) return dataArr;
	return attachLevelTotals(dataArr, snapshots, currentLevel, keySelector, cardTitles);
};

// Special attachment for daysData where items have _id (day key) but patients have day property
const attachDaysLevelTotals = (list, snapshots, currentLevel, cardTitles = {}) => {
	if (!Array.isArray(list) || currentLevel < 2 || !snapshots) return list;
	const level1 = Array.isArray(snapshots.level1) ? snapshots.level1 : [];
	const level2 = Array.isArray(snapshots.level2) ? snapshots.level2 : [];
	const level3 = Array.isArray(snapshots.level3) ? snapshots.level3 : [];
	const level4 = Array.isArray(snapshots.level4) ? snapshots.level4 : [];
	const level5 = Array.isArray(snapshots.level5) ? snapshots.level5 : [];
	return list.map((item) => {
		// Preserve existing level totals if they're already set
		if (item.level1Total !== undefined || item.level2Total !== undefined || item.level3Total !== undefined || item.level4Total !== undefined || item.level5Total !== undefined) {
			return {
				...item,
				...(currentLevel >= 2 && cardTitles.level1 && { level1CardTitle: cardTitles.level1 }),
				...(currentLevel >= 3 && cardTitles.level2 && { level2CardTitle: cardTitles.level2 }),
				...(currentLevel >= 4 && cardTitles.level3 && { level3CardTitle: cardTitles.level3 }),
				...(currentLevel >= 5 && cardTitles.level4 && { level4CardTitle: cardTitles.level4 }),
				...(currentLevel >= 6 && cardTitles.level5 && { level5CardTitle: cardTitles.level5 }),
			};
		}
		const dayKey = item._id ?? item.id;
		if (dayKey === undefined || dayKey === null) return item;
		const level1Total = level1.filter((p) => p.day === dayKey).length;
		const level2Total = level2.filter((p) => p.day === dayKey).length;
		const level3Total = level3.filter((p) => p.day === dayKey).length;
		const level4Total = level4.filter((p) => p.day === dayKey).length;
		const level5Total = level5.filter((p) => p.day === dayKey).length;
		return {
			...item,
			...(currentLevel >= 2 && { level1Total }),
			...(currentLevel >= 3 && { level2Total }),
			...(currentLevel >= 4 && { level3Total }),
			...(currentLevel >= 5 && { level4Total }),
			...(currentLevel >= 6 && { level5Total }),
			...(currentLevel >= 2 && cardTitles.level1 && { level1CardTitle: cardTitles.level1 }),
			...(currentLevel >= 3 && cardTitles.level2 && { level2CardTitle: cardTitles.level2 }),
			...(currentLevel >= 4 && cardTitles.level3 && { level3CardTitle: cardTitles.level3 }),
			...(currentLevel >= 5 && cardTitles.level4 && { level4CardTitle: cardTitles.level4 }),
			...(currentLevel >= 6 && cardTitles.level5 && { level5CardTitle: cardTitles.level5 }),
		};
	});
};

export async function getTotalNumber(data) {
	if (data.length > 0) {
		const transferTypeTotal = _.countBy(data, "isOutgoing");
		let totalOutgoing = 0;
		let totalIncoming = 0;

		totalOutgoing = transferTypeTotal?.true || 0;
		totalIncoming = transferTypeTotal?.false || 0;
		const n = totalIncoming - totalOutgoing;
		return n;
	}
	return 0;
}

export async function getListData(dataGroupBy, type, transferType, totalPatient, totalFilter) {
	let listGroup = [];
	const { originalData = [], totalType = null } = totalFilter;

	if (dataGroupBy) {
		for (const [key, value] of Object.entries(dataGroupBy)) {
			const valueArr = value[0];
			let totalCountValue = value.length;
			let isTotalPercentage = false;
			if (!transferType || transferType === OVERALL_PAGE_SUB_TYPE.TOTAL) {
				totalCountValue = await getTotalNumber(value);
			}
			let original = originalData[key] ? originalData[key]?.length : 0;
			if (!transferType || transferType === OVERALL_PAGE_SUB_TYPE.TOTAL) {
				original = await getTotalNumber(originalData[key] ? originalData[key] : []);
				isTotalPercentage = true;
			}

			if (key && valueArr && valueArr[type]) {
				let object = Object();
				object._id = key;
				object.id = key;
				object.isTotalPercentage = isTotalPercentage;
				object.label = valueArr[type].label;
				object.name = valueArr[type].label;
				object.total = totalCountValue;
				object.value = value.length;
				object.originalTotal = original;
				object.isTooltip = totalType && totalType === TOTAL_TYPE.MAIN ? false : true;
				object.percentage = itemPercentage(value.length, totalPatient, "number");
				listGroup.push(object);
			}
		}

		if (listGroup.length > 0) {
			if (!transferType || transferType === OVERALL_PAGE_SUB_TYPE.TOTAL) {
				listGroup = _.orderBy(listGroup, "total", "desc").map((item) => ({
					...item,
					total: new Intl.NumberFormat("en-US", { signDisplay: "exceptZero" }).format(item.total),
				}));
			} else {
				listGroup = _.orderBy(listGroup, "total", "desc");
			}
		}
	}

	return listGroup;
}

export async function plannedFilter(patientData, oldFilter, type = null) {
	patientData = _.filter(patientData, ({ floorId, doctorId, insuranceId }) => {
		let filterArr = [];

		if (oldFilter.insuranceData.length > 0 && type !== "insuranceData") {
			filterArr.push(_.includes(oldFilter.insuranceData, insuranceId));
		}
		if (oldFilter.doctorData.length > 0 && type !== "doctorData") {
			filterArr.push(_.includes(oldFilter.doctorData, doctorId));
		}
		if (oldFilter.floorsData.length > 0 && type !== "floorsData") {
			filterArr.push(_.includes(oldFilter.floorsData, floorId));
		}
		return _.every(filterArr);
	});
	return patientData;
}

// * added comparison
export async function updateFacilityPercentageTotal(data, forComparison) {
	let facilityWiseTotal = _(data)
		.groupBy("facilityId")
		.sortBy((group) => data.indexOf(group[0]))
		.map((product) => {
			return {
				id: product[0]?.facilityId,
				total: product.length || 0,
			};
		})
		.value();
	forComparison
		? store.dispatch(setFacilityPercentageComparison(facilityWiseTotal))
		: store.dispatch(setFacilityPercentage(facilityWiseTotal));
}

// * added comparison
export async function updateFilterListData(cardFilter, patientList, priorityData = [], transferType, forComparison, isExhaustFilterActive = false) {
	const { list = [], ninetyDaysData, exhustListData } = patientList || {};

	const {
		dbData,
		lockedTotal,
		mainNumPercentage,
		lockedTotalBy,
		isExhaustFilterActive: storeExhaustFilterActive,
	} = forComparison ? store.getState().overallComparison : store.getState().overall;

	// Use passed parameter if provided, otherwise use store value
	const exhaustFilterActive = isExhaustFilterActive !== undefined ? isExhaustFilterActive : (storeExhaustFilterActive || false);
	const customTabs = patientList?.customTabs ?? [];
	const diffDashboardPatients = patientList?.diffDashboardPatients ?? [];
	const customCombineTabData = patientList?.customCombineTabData ?? [];

	let censusAverage = dbData?.censusAverage;

	if (!forComparison) {
		censusAverage = await getDynamicPercentageBy(dbData);
	}

	let mainNumPercentageSelected = mainNumPercentage;
	let objectCustom = Object();
	let newSavedFilters = [];

	// If exhaust filter is active, use exhustListData as base, otherwise use regular list
	let patientFilterData = exhaustFilterActive && Array.isArray(exhustListData) && exhustListData.length > 0
		? _.cloneDeep(exhustListData)
		: list;

	let lockedTotalModified = lockedTotal;
	let isComparingAgainstAvgCensus = true;

	// Apply transferType filtering (works for both regular list and exhustListData)
	if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_INCOMING) {
		patientFilterData = _.filter(patientFilterData, { isOutgoing: false });
	}
	if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING) {
		patientFilterData = _.filter(patientFilterData, {
			isOutgoing: true,
		});
	}
	// When exhaust filter is active, originalData should also come from exhausted list
	let totalFilterData = {
		originalData: exhaustFilterActive && Array.isArray(exhustListData) && exhustListData.length > 0
			? _.cloneDeep(exhustListData)
			: patientFilterData,
		totalType: TOTAL_TYPE.FILTER,
		transferType,
		totalForPercentage: censusAverage,
		isExhaustFilterActive: exhaustFilterActive,
	};
	if (transferType) {
		totalFilterData.totalForPercentage = patientFilterData?.length;
		isComparingAgainstAvgCensus = false;
	}
	// if (mainNumPercentageSelected) {
	//     totalFilterData.totalForPercentage = mainNumPercentageSelected
	// }

	if (lockedTotalBy) {
		const newLockedTotalBy = null;
		const newLockTotal = null;
		if (
			lockedTotalBy === OVERALL_PAGE_SUB_TYPE.TOTAL_INCOMING ||
			lockedTotalBy === OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING ||
			lockedTotalBy === OVERALL_PAGE_SUB_TYPE.TOTAL ||
			lockedTotalBy === "census"
		) {
			if (lockedTotalModified && !transferType && lockedTotalBy !== "census") {
				if (forComparison) {
					const currentLockedTotalBy = store.getState().overallComparison.lockedTotalBy;
					if (!_.isEqual(currentLockedTotalBy, newLockedTotalBy)) {
						store.dispatch(setLockedTotalByComparison(newLockedTotalBy))
					}
				} else {
					const currentLockedTotalBy = store.getState().admission.lockedTotalBy;
					if (!_.isEqual(currentLockedTotalBy, newLockedTotalBy)) {
						store.dispatch(setLockedTotalBy(newLockedTotalBy))
					}
				}

				if (forComparison) {
					const currentLockedTotal = store.getState().overallComparison.lockedTotal;
					if (!_.isEqual(currentLockedTotal, newLockTotal)) {
						store.dispatch(setLockTotalComparison(newLockTotal))
					}
				} else {
					const currentLockedTotal = store.getState().admission.lockedTotal;
					if (!_.isEqual(currentLockedTotal, newLockTotal)) {
						store.dispatch(setLockTotal(newLockTotal))
					}
				}

				lockedTotalModified = null;
			}
			if (!lockedTotalModified) {
				forComparison ? store.dispatch(setLockedTotalByComparison(null)) : store.dispatch(setLockedTotalBy(null));
				forComparison ? store.dispatch(setLockTotalComparison(null)) : store.dispatch(setLockTotal(null));
				lockedTotalModified = null;
			}
		} else {
			const lockedFilterRemoved = _.find(priorityData, { type: lockedTotalBy });
			if (!lockedFilterRemoved) {
				if (forComparison) {
					const currentLockedTotalBy = store.getState().overallComparison.lockedTotalBy;
					if (!_.isEqual(currentLockedTotalBy, newLockedTotalBy)) {
						store.dispatch(setLockedTotalByComparison(newLockedTotalBy))
					}
				} else {
					const currentLockedTotalBy = store.getState().admission.lockedTotalBy;
					if (!_.isEqual(currentLockedTotalBy, newLockedTotalBy)) {
						store.dispatch(setLockedTotalBy(newLockedTotalBy))
					}
				}

				if (forComparison) {
					const currentLockedTotal = store.getState().overallComparison.lockedTotal;
					if (!_.isEqual(currentLockedTotal, newLockTotal)) {
						store.dispatch(setLockTotalComparison(newLockTotal))
					}
				} else {
					const currentLockedTotal = store.getState().admission.lockedTotal;
					if (!_.isEqual(currentLockedTotal, newLockTotal)) {
						store.dispatch(setLockTotal(newLockTotal))
					}
				}

				lockedTotalModified = null;
			}
		}
	}
	const newPayload = [];
	if (forComparison) {
		const currentFacilityPercentage = store.getState().overallComparison.facilityPercentage;
		if (!_.isEqual(currentFacilityPercentage, newPayload)) {
			store.dispatch(setFacilityPercentageComparison(newPayload))
		}
	} else {
		const currentFacilityPercentage = store.getState().admission.facilityPercentage;
		if (!_.isEqual(currentFacilityPercentage, newPayload)) {
			store.dispatch(setFacilityPercentage(newPayload))
		}
	}

	if (!lockedTotalModified) {
		const newSetIsCensusTotalLocked = false;
		if (forComparison) {
			const currentIsCensusTotalLocked = store.getState().overallComparison.isCensusTotalLocked;
			if (!_.isEqual(currentIsCensusTotalLocked, newSetIsCensusTotalLocked)) {
				store.dispatch(setIsCensusTotalLockedComparison(newSetIsCensusTotalLocked))
			}
		} else {
			const currentIsCensusTotalLocked = store.getState().admission.isCensusTotalLocked;
			if (!_.isEqual(currentIsCensusTotalLocked, newSetIsCensusTotalLocked)) {
				store.dispatch(setIsCensusTotalLocked(newSetIsCensusTotalLocked));
			}
		}

		if (forComparison) {
			const currentLockedTotalBy = store.getState().overallComparison.lockedTotalBy;
			if (!_.isEqual(currentLockedTotalBy, null)) {
				store.dispatch(setLockedTotalByComparison(null))
			}
		} else {
			const currentLockedTotalBy = store.getState().admission.lockedTotalBy;
			if (!_.isEqual(currentLockedTotalBy, null)) {
				store.dispatch(setLockedTotalBy(null))
			}
		}
	}

	if (lockedTotalModified) {
		totalFilterData.totalForPercentage = lockedTotalModified;
	}

	if (lockedTotalModified && priorityData.length === 1 && !transferType) {
		totalFilterData.totalForPercentage = lockedTotalModified;
	}

	if (priorityData?.length > 0) {
		isComparingAgainstAvgCensus = false;
	}

	if (
		(priorityData?.length > 0 && (!lockedTotalBy || !lockedTotal)) ||
		(priorityData?.length > 0 &&
			lockedTotalBy !== "census" &&
			lockedTotalBy !== OVERALL_PAGE_SUB_TYPE.TOTAL_INCOMING &&
			lockedTotalBy !== OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING &&
			lockedTotalBy !== OVERALL_PAGE_SUB_TYPE.TOTAL)
	) {
		totalFilterData.totalForPercentage = null;
	}
	let customTabsObj = {};
	if (priorityData?.length > 0) {
		let i = 0;
		// Collect intermediate patient sets before each filter is applied (for tooltip only).
		// This does not change any existing calculations; it's read-only data for UI.
		const intermediateTotals = {};
		const cardTitles = {};
		for await (const ele of priorityData) {
			i++;
			if (priorityData?.length === i && lockedTotalModified && !lockedTotalBy) {
				forComparison
					? store.dispatch(setLockedTotalByComparison(ele.type))
					: store.dispatch(setLockedTotalBy(ele.type));
			}

			// Store snapshot before applying the current filter so downstream cards can show level totals.
			// Support up to 5 levels: level1, level2, level3, level4, level5
			if (i > 1 && i <= 6) {
				intermediateTotals[`level${i - 1}`] = patientFilterData;
				// Store card title for this level from the previous filter (i-2 because i is 1-indexed and we want the filter that was just applied)
				// When i=2, we want the card title from priorityData[0] (first filter) -> level1
				// When i=3, we want the card title from priorityData[1] (second filter) -> level2
				// When i=4, we want the card title from priorityData[2] (third filter) -> level3
				// When i=5, we want the card title from priorityData[3] (fourth filter) -> level4
				// When i=6, we want the card title from priorityData[4] (fifth filter) -> level5
				const previousFilter = priorityData[i - 2];
				const cardTitle = previousFilter?.cardTitle || previousFilter?.type || '';
				cardTitles[`level${i - 1}`] = cardTitle ? `${cardTitle} total` : '';
			}

			if (ele?.question?.isCustom) {
				if (customTabs.length > 0) {
					const customTabsRes = customTabs.filter((x) => x.accessor === ele.type);
					if (customTabsRes.length > 0) {
						// For combineTab, always base percentages on original data length
						// to keep percentage consistent, same as Hospital page.
						const isCombineTab = ele?.question?.customTab?.type === CUSTOM_TAB_TYPE.combineTab;
						const originalDataLength = totalFilterData?.originalData?.length || patientFilterData.length;
						const totalForPercentage = isCombineTab
							? (mainNumPercentageSelected || originalDataLength || totalFilterData.totalForPercentage)
							: (mainNumPercentageSelected && i === 1 ? mainNumPercentageSelected : totalFilterData.totalForPercentage);

						customTabsObj = await getCustomTabsCards(
							patientFilterData,
							customTabsRes,
							{
								...totalFilterData,
								totalForPercentage,
								intermediateTotals,
								currentLevel: i,
								cardTitles: {
									level1: cardTitles.level1,
									level2: cardTitles.level2,
									level3: cardTitles.level3,
									level4: cardTitles.level4,
									level5: cardTitles.level5,
								},
							},
							{
								ninetyDaysData,
								dynamicCards: [],
								diffDashboardPatients,
								pageType: PAGE_TYPE.OVERALL,
								forComparison,
								customCombineTabData,
								exhustListData,
							}
						);
					}
				}
				objectCustom = { ...objectCustom, ...customTabsObj };

				patientFilterData = filterCustomPatientData(patientFilterData, cardFilter, ele);
			}

			if (ele.type === OVERALL_CARDS_TYPE.DOCTOR_DATA) {
				objectCustom.doctorData = await doctorData(patientFilterData, {
					...totalFilterData,
					totalType: TOTAL_TYPE.MAIN,
					totalForPercentage:
						mainNumPercentageSelected && i === 1 ? mainNumPercentageSelected : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: i,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				});
				objectCustom.doctorData = attachLevelTotals(
					objectCustom.doctorData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					i,
					(p) => p.doctorId ?? p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);

				patientFilterData = _.filter(patientFilterData, ({ doctorId }) =>
					_.every([_.includes(cardFilter.doctorData, doctorId)])
				);
			}
			if (ele.type === OVERALL_CARDS_TYPE.DAYS_DATA) {
				objectCustom.daysData = await daysData(patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: i,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				});
				objectCustom.daysData = attachDaysLevelTotals(
					objectCustom.daysData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					i,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);
				patientFilterData = _.filter(patientFilterData, ({ day }) => _.every([_.includes(cardFilter.daysData, day)]));
			}
			if (ele.type === OVERALL_CARDS_TYPE.FLOORS_DATA) {
				objectCustom.floorsData = await floorsData(patientFilterData, {
					...totalFilterData,
					totalType: TOTAL_TYPE.MAIN,
					totalForPercentage:
						mainNumPercentageSelected && i === 1 ? mainNumPercentageSelected : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: i,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				});
				objectCustom.floorsData = attachLevelTotals(
					objectCustom.floorsData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					i,
					(p) => p.floorId ?? p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);

				patientFilterData = _.filter(patientFilterData, ({ floorId }) =>
					_.every([_.includes(cardFilter.floorsData, floorId)])
				);
			}

			if (ele.type === OVERALL_CARDS_TYPE.INSURANCE_DATA) {
				objectCustom.insuranceData = await insuranceData(patientFilterData, {
					...totalFilterData,
					exhustListData: exhustListData || [],
					totalType: TOTAL_TYPE.MAIN,
					totalForPercentage:
						mainNumPercentageSelected && i === 1 ? mainNumPercentageSelected : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: i,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				});
				objectCustom.insuranceData = attachLevelTotals(
					objectCustom.insuranceData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					i,
					(p) => p.insuranceId ?? p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);

				// Filter regular patients by selected insurance
				patientFilterData = _.filter(patientFilterData, ({ insuranceId }) =>
					_.every([_.includes(cardFilter.insuranceData, insuranceId)])
				);
			}

			// Medicare Exhaust insurance card
			if (ele.type === OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA) {
				// Use global exhausted list from backend (already filtered by date/facility on server)
				objectCustom.exhaustInsuranceData = await exhaustInsuranceData(exhustListData || [], {
					...totalFilterData,
					// For exhaust card, originalData should also come from exhausted list
					originalData: exhustListData || [],
					totalType: TOTAL_TYPE.MAIN,
					totalForPercentage:
						mainNumPercentageSelected && i === 1 ? mainNumPercentageSelected : totalFilterData.totalForPercentage,
					useExhaustListData: true,
					intermediateTotals,
					currentLevel: i,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				});

				// For exhaustInsuranceData we don't further filter patientFilterData,
				// since it is a summary card over Medicare-exhausted patients.
			}

			if (ele.type === OVERALL_CARDS_TYPE.NINETY_DAYS_DATA) {
				// Use special function for exhaust filter, otherwise use regular function
				if (exhaustFilterActive) {
					objectCustom.ninetyDaysData = await ninetyDaysDataListForExhaust(patientFilterData, {
						...totalFilterData,
						totalType: TOTAL_TYPE.MAIN,
						totalForPercentage:
							mainNumPercentageSelected && i === 1 ? mainNumPercentageSelected : totalFilterData.totalForPercentage,
						intermediateTotals,
						currentLevel: i,
						cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
					}, ninetyDaysData);
					objectCustom.ninetyDaysData = maybeAttachTotals(
						objectCustom.ninetyDaysData,
						{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
						i,
						(p) => p._id ?? p.id,
						{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
					);
					// Filter patientFilterData based on selected ninetyDaysData ranges
					if (cardFilter.ninetyDaysData && cardFilter.ninetyDaysData.length > 0) {
						const selectedRanges = cardFilter.ninetyDaysData;
						patientFilterData = _.filter(patientFilterData, (p) => {
							const count = p.latestObjExhaust?.count != null
								? p.latestObjExhaust.count
								: (p.dateOfADT && p.exhaustionDate
									? moment(p.exhaustionDate).startOf('day').diff(moment(p.dateOfADT).startOf('day'), 'days') + 1
									: null);
							if (count == null) return false;
							return selectedRanges.some((rangeLabel) => {
								// Parse label to extract min and max days (e.g., "0 - 7 Days" -> [0, 7])
								const numbers = rangeLabel.match(/\d+/g);
								if (numbers && numbers.length >= 1) {
									const min = parseInt(numbers[0], 10);
									const max = numbers.length >= 2 ? parseInt(numbers[1], 10) : Infinity;
									return count >= min && count <= max;
								}
								return false;
							});
						});
					}
				} else {
					objectCustom.ninetyDaysData = await ninetyDaysDataList(ninetyDaysData, patientFilterData, {
						...totalFilterData,
						totalType: TOTAL_TYPE.MAIN,
						totalForPercentage:
							mainNumPercentageSelected && i === 1 ? mainNumPercentageSelected : totalFilterData.totalForPercentage,
						intermediateTotals,
						currentLevel: i,
						cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
					});
					objectCustom.ninetyDaysData = maybeAttachTotals(
						objectCustom.ninetyDaysData,
						{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
						i,
						(p) => p._id ?? p.id,
						{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
					);
					patientFilterData = await ninetyDaysDataFilter(cardFilter.ninetyDaysData, patientFilterData, ninetyDaysData);
				}
			}
			newSavedFilters = [...newSavedFilters, ele.type];
		}

		if (transferType && transferType !== "total") {
			forComparison
				? store.dispatch(
					setFilterTotalComparison(
						lockedTotalModified
							? lockedTotalModified
							: mainNumPercentageSelected
								? mainNumPercentageSelected
								: patientFilterData.length
					)
				)
				: store.dispatch(
					setFilterTotal(
						lockedTotalModified
							? lockedTotalModified
							: mainNumPercentageSelected
								? mainNumPercentageSelected
								: patientFilterData.length
					)
				);
		}

		const totalPercentageCount = lockedTotalModified ? lockedTotalModified : patientFilterData.length; //transferType ? patientFilterData?.length : censusAverage

		// If insurance filter is applied but insurance is not in priorityData,
		// filter patientFilterData by selected insurance
		if (!_.includes(newSavedFilters, OVERALL_CARDS_TYPE.INSURANCE_DATA) && 
			cardFilter.insuranceData && Array.isArray(cardFilter.insuranceData) && cardFilter.insuranceData.length > 0) {
			// Filter regular patients by selected insurance
			patientFilterData = _.filter(patientFilterData, ({ insuranceId }) =>
				_.every([_.includes(cardFilter.insuranceData, insuranceId)])
			);
		}

		if (!_.includes(newSavedFilters, OVERALL_CARDS_TYPE.INSURANCE_DATA)) {
			objectCustom.insuranceData = await insuranceData(patientFilterData, {
				...totalFilterData,
				exhustListData: exhustListData || [],
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: priorityData?.length || 0,
				cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
			});
			objectCustom.insuranceData = attachLevelTotals(
				objectCustom.insuranceData,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				priorityData?.length || 0,
				(p) => p.insuranceId ?? p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		}
		if (!_.includes(newSavedFilters, OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA)) {
			objectCustom.exhaustInsuranceData = await exhaustInsuranceData(exhustListData || [], {
				...totalFilterData,
				totalForPercentage: totalPercentageCount,
				originalData: exhustListData || [],
				useExhaustListData: true,
				intermediateTotals,
				currentLevel: priorityData?.length || 0,
				cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
			});
		}
		if (!_.includes(newSavedFilters, OVERALL_CARDS_TYPE.FLOORS_DATA)) {
			objectCustom.floorsData = await floorsData(patientFilterData, {
				...totalFilterData,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: priorityData?.length || 0,
				cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
			});
			objectCustom.floorsData = attachLevelTotals(
				objectCustom.floorsData,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				priorityData?.length || 0,
				(p) => p.floorId ?? p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		}
		if (!_.includes(newSavedFilters, OVERALL_CARDS_TYPE.DOCTOR_DATA)) {
			objectCustom.doctorData = await doctorData(patientFilterData, {
				...totalFilterData,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: priorityData?.length || 0,
				cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
			});
			objectCustom.doctorData = attachLevelTotals(
				objectCustom.doctorData,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				priorityData?.length || 0,
				(p) => p.doctorId ?? p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		}
		if (!_.includes(newSavedFilters, OVERALL_CARDS_TYPE.NINETY_DAYS_DATA)) {
			// Use special function for exhaust filter, otherwise use regular function
			if (isExhaustFilterActive) {
				objectCustom.ninetyDaysData = await ninetyDaysDataListForExhaust(patientFilterData, {
					...totalFilterData,
					totalForPercentage: totalPercentageCount,
					intermediateTotals,
					currentLevel: priorityData?.length || 0,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				}, ninetyDaysData);
				objectCustom.ninetyDaysData = maybeAttachTotals(
					objectCustom.ninetyDaysData,
					{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
					priorityData?.length || 0,
					(p) => p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);
			} else {
				objectCustom.ninetyDaysData = await ninetyDaysDataList(ninetyDaysData, patientFilterData, {
					...totalFilterData,
					totalForPercentage: totalPercentageCount,
					intermediateTotals,
					currentLevel: priorityData?.length || 0,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				});
				objectCustom.ninetyDaysData = maybeAttachTotals(
					objectCustom.ninetyDaysData,
					{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
					priorityData?.length || 0,
					(p) => p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);
			}
		}
		if (!_.includes(newSavedFilters, OVERALL_CARDS_TYPE.DAYS_DATA)) {
			objectCustom.daysData = await daysData(patientFilterData, {
				...totalFilterData,
				totalType: TOTAL_TYPE.FILTER,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: priorityData?.length || 0,
				cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
			});
			objectCustom.daysData = attachDaysLevelTotals(
				objectCustom.daysData,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				priorityData?.length || 0,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		}
		if (customTabs.length > 0) {
			let customTabsObjRes = {};
			const isCheckNewFilters = true;
			customTabsObjRes = await getCustomTabsCards(
				patientFilterData,
				customTabs,
				{ ...totalFilterData, totalType: TOTAL_TYPE.FILTER, totalForPercentage: totalPercentageCount, intermediateTotals, currentLevel: priorityData?.length || 0, cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 } },
				{ ninetyDaysData, dynamicCards: [], newSavedFilters, isCheckNewFilters, objectCustom, pageType: PAGE_TYPE.OVERALL, diffDashboardPatients, forComparison, customCombineTabData, exhustListData }
			);
			objectCustom = { ...objectCustom, ...customTabsObjRes };
		}
		if (!lockedTotalModified) {
			if (forComparison) {
				const currentLockedByFacility = store.getState().overallComparison.lockedByFacility;
				if (!_.isEqual(currentLockedByFacility ?? [], patientFilterData ?? [])) {
					store.dispatch(setLockedByFacilityComparison(patientFilterData));
				}
			} else {
				const currentLockedByFacility = store.getState().admission.lockedByFacility;
				if (!_.isEqual(currentLockedByFacility ?? [], patientFilterData ?? [])) {
					store.dispatch(setLockedByFacility(patientFilterData));
				}
			}
		}
	} else {
		batch(async () => {
			totalFilterData.totalType = TOTAL_TYPE.MAIN;
			const totalCount = mainNumPercentageSelected
				? mainNumPercentageSelected
				: transferType && transferType !== "total"
					? patientFilterData.length
					: null;

			let totalCountPer = lockedTotalModified ? lockedTotalModified : transferType ? totalCount : censusAverage;
			if (forComparison) {
				const currentFilterTotal = store.getState().overallComparison.filterTotal;
				if (!_.isEqual(currentFilterTotal, totalCountPer)) {
					store.dispatch(setFilterTotalComparison(totalCountPer));
				}
			} else {
				const currentFilterTotal = store.getState().admission.filterTotal;
				if (!_.isEqual(currentFilterTotal, totalCountPer)) {
					store.dispatch(setFilterTotal(totalCountPer));
				}
			};

			updateFacilityPercentageTotal(patientFilterData, forComparison);
			if (lockedTotalModified && !lockedTotalBy) {
				if (transferType) {
					forComparison
						? store.dispatch(setLockedTotalByComparison(transferType))
						: store.dispatch(setLockedTotalBy(transferType));
				} else {
					forComparison
						? store.dispatch(setLockedTotalByComparison("census"))
						: store.dispatch(setLockedTotalBy("census"));
				}
			}
			if (transferType !== "total" && lockedTotalModified && mainNumPercentage) {
				var isCensusTotalLocked = true;
				if (forComparison) {
					const currentIsCensusTotalLocked = store.getState().overallComparison.isCensusTotalLocked;
					if (!_.isEqual(currentIsCensusTotalLocked, isCensusTotalLocked)) {
						store.dispatch(setIsCensusTotalLockedComparison(isCensusTotalLocked));
					}
				} else {
					const currentIsCensusTotalLocked = store.getState().admission.isCensusTotalLocked;
					if (!_.isEqual(currentIsCensusTotalLocked, isCensusTotalLocked)) {
						store.dispatch(setIsCensusTotalLocked(isCensusTotalLocked));
					}
				};
			}
			if (!lockedTotalModified) {
				if (forComparison) {
					const currentLockedByFacility = store.getState().overallComparison.lockedByFacility;
					if (!_.isEqual(currentLockedByFacility ?? [], patientFilterData ?? [])) {
						store.dispatch(setLockedByFacilityComparison(patientFilterData));
					}
				} else {
					const currentLockedByFacility = store.getState().admission.lockedByFacility;
					if (!_.isEqual(currentLockedByFacility ?? [], patientFilterData ?? [])) {
						store.dispatch(setLockedByFacility(patientFilterData));
					}
				}
			}
			totalFilterData.totalForPercentage = totalCountPer;
		});

		// If insurance filter is applied, filter patientFilterData by selected insurance
		if (cardFilter.insuranceData && Array.isArray(cardFilter.insuranceData) && cardFilter.insuranceData.length > 0) {
			// Filter regular patients by selected insurance
			patientFilterData = _.filter(patientFilterData, ({ insuranceId }) =>
				_.every([_.includes(cardFilter.insuranceData, insuranceId)])
			);
		}

		// Use special function for ninetyDaysData when exhaust filter is active
		const ninetyDaysDataPromise = exhaustFilterActive
			? ninetyDaysDataListForExhaust(patientFilterData, { ...totalFilterData, intermediateTotals: {}, currentLevel: 0 }, ninetyDaysData)
			: ninetyDaysDataList(ninetyDaysData, patientFilterData, { ...totalFilterData, intermediateTotals: {}, currentLevel: 0 });

		[objectCustom.insuranceData, objectCustom.floorsData, objectCustom.doctorData, objectCustom.ninetyDaysData, objectCustom.daysData, objectCustom.exhaustInsuranceData] =
			await Promise.all([
				insuranceData(patientFilterData, { ...totalFilterData, exhustListData: exhustListData || [], intermediateTotals: {}, currentLevel: 0 }),
				floorsData(patientFilterData, { ...totalFilterData, intermediateTotals: {}, currentLevel: 0 }),
				doctorData(patientFilterData, { ...totalFilterData, intermediateTotals: {}, currentLevel: 0 }),
				ninetyDaysDataPromise,
				daysData(patientFilterData, { ...totalFilterData, intermediateTotals: {}, currentLevel: 0 }),
				// Exhaust card should be based on global exhausted list data
				exhaustInsuranceData(exhustListData || [], {
					...totalFilterData,
					originalData: exhustListData || [],
					useExhaustListData: true,
					intermediateTotals: {},
					currentLevel: 0,
				}),
			]);

		if (customTabs.length > 0) {
			customTabsObj = await getCustomTabsCards(
				patientFilterData,
				customTabs,
				{
					...totalFilterData,
					intermediateTotals: {},
					currentLevel: 0,
					cardTitles: {}
				}, {
					ninetyDaysData, dynamicCards: [], pageType: PAGE_TYPE.OVERALL, diffDashboardPatients, forComparison, customCombineTabData,
				exhustListData
			});
		}

		objectCustom = { ...objectCustom, ...customTabsObj }
	}
	objectCustom.isComparingAgainstAvgCensus = isComparingAgainstAvgCensus;

	// Page converter uses main dashboard only (comparison runs do not attach this field).
	if (!forComparison) {
		const pageConverterRows = Array.isArray(patientFilterData) ? patientFilterData : [];
		return {
			...objectCustom,
			pageConverterFilteredPatients: _.cloneDeep(pageConverterRows),
		};
	}
	return objectCustom;
}

async function ninetyDaysDataFilter(cardFilter, patientData, ninetyDaysData) {
	let ninetyDaysDataIds = [];
	let ninetyDaysDataFilter = _.filter(ninetyDaysData, ({ _id }) => _.every([_.includes(cardFilter, _id)]));
	if (ninetyDaysDataFilter && ninetyDaysDataFilter.length > 0) {
		ninetyDaysDataFilter.map((item) => (ninetyDaysDataIds = [...ninetyDaysDataIds, ...item.ids]));
	}
	patientData = _.filter(patientData, ({ _id }) => _.every([_.includes(ninetyDaysDataIds, _id)]));
	return patientData;
}

// Special function for ninetyDaysData when exhaust filter is active
// Uses the existing ninetyDaysData structure and updates values based on exhaust patients' count
async function ninetyDaysDataListForExhaust(exhaustedPatients, totalFilterData, ninetyDaysData) {
	// Use the existing ninetyDaysData structure from backend
	if (!Array.isArray(ninetyDaysData) || ninetyDaysData.length === 0) {
		return [];
	}

	// Clone the structure to avoid mutating the original
	const result = ninetyDaysData.map((item) => ({
		...item,
		ids: [],
		admissionIds: [],
		value: 0,
		total: 0,
		originalTotal: 0,
		percentage: 0,
	}));

	// If no exhausted patients, return structure with all zeros
	if (!exhaustedPatients || !Array.isArray(exhaustedPatients) || exhaustedPatients.length === 0) {
		const percentageTotal = totalFilterData?.totalForPercentage || 0;
		return result.map((item) => ({
			...item,
			percentage: itemPercentage(0, percentageTotal, "number"),
		}));
	}

	// Group exhausted patients by their count (days from dateOfADT to exhaustionDate)
	const countGroups = _.groupBy(exhaustedPatients, (p) => {
		// Use the count field if available (pre-calculated in backend)
		if (p.latestObjExhaust?.count != null) {
			return p.latestObjExhaust.count;
		}
		// Fallback: calculate from dateOfADT and exhaustionDate
		if (p.dateOfADT && p.exhaustionDate) {
			const start = moment(p.dateOfADT).startOf('day');
			const end = moment(p.exhaustionDate).startOf('day');
			if (start.isValid() && end.isValid()) {
				return end.diff(start, "days") + 1; // Inclusive
			}
		}
		return null;
	});

	const percentageTotal = totalFilterData?.totalForPercentage || exhaustedPatients.length;

	// Parse label to extract min and max days for each range
	// Labels can be like "0 - 7 Days", "8 - 14 Days", "15 - 30 Days", "31 - 60 Days", "61 - 90 Days"
	result.forEach((rangeItem) => {
		const label = rangeItem.label || rangeItem.name || '';
		// Extract numbers from label (e.g., "0 - 7 Days" -> [0, 7])
		const numbers = label.match(/\d+/g);

		if (numbers && numbers.length >= 1) {
			const min = parseInt(numbers[0], 10);
			const max = numbers.length >= 2 ? parseInt(numbers[1], 10) : Infinity;

			let patientIds = [];
			let count = 0;

			// Count patients in this range
			Object.keys(countGroups).forEach((countKey) => {
				if (countKey === 'null') return; // Skip patients without valid count
				const days = parseInt(countKey, 10);
				if (!isNaN(days) && days >= min && days <= max) {
					const patientsInRange = countGroups[countKey];
					patientsInRange.forEach((p) => {
						const id = p.id || p._id;
						if (id) {
							patientIds.push(id);
							count += 1;
						}
					});
				}
			});

			// Update the range item with calculated values
			rangeItem.value = count;
			rangeItem.total = count;
			rangeItem.originalTotal = count;
			rangeItem.percentage = itemPercentage(count, percentageTotal, "number");
			rangeItem.ids = patientIds;
		}
	});

	return result;
}

async function ninetyDaysDataList(data, patients, totalFilterData) {
	const patientIds = patients && patients.length > 0 ? patients.map((item) => item.id) : [];
	let percentageTotal = totalFilterData?.totalForPercentage ? totalFilterData.totalForPercentage : patients?.length;
	const res = await updateListTotalValue(data, patientIds, "value", percentageTotal, totalFilterData);
	return res;
}

async function insuranceData(data, totalFilter) {
	const { transferType = null, exhustListData = [], isExhaustFilterActive = false } = totalFilter;
	//insuranceData Cal
	let insuranceDBDataGroup = [];
	
	// When exhaust filter is active, use only exhustListData (data is already exhustListData)
	// For INCOMING: Don't combine exhaust data in initial grouping - they should only show secondary insurance
	// For OUTGOING: Combine regular data with exhaust data before grouping (only if exhaust filter is NOT active)
	// For TOTAL: Combine regular data with exhaust data before grouping (only if exhaust filter is NOT active)
	let combinedData = data || [];
	let combinedOriginalData = totalFilter.originalData || [];
	
	// For OUTGOING and TOTAL, combine exhaust data with regular data before grouping (only if exhaust filter is NOT active)
	if (!isExhaustFilterActive && (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING || transferType === OVERALL_PAGE_SUB_TYPE.TOTAL || !transferType || transferType === "total")) {
		if (Array.isArray(exhustListData) && exhustListData.length > 0) {
			let filteredExhaustData = [];
			if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING) {
				filteredExhaustData = _.filter(exhustListData, { isOutgoing: true });
			} else {
				// TOTAL or no transfer type - include ALL exhaust data
				filteredExhaustData = exhustListData;
			}
			
			// For TOTAL: Don't include exhaust patients with Medicare in initial grouping
			// They will be processed later to increment secondary insurance only
			// For OUTGOING: Include all exhaust data with Medicare
			if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL || !transferType || transferType === "total") {
				// Filter out exhaust patients with Medicare as payerSourceInsurance for TOTAL
				// We only want to process them for secondary insurance increment, not create Medicare entries
				const exhaustWithoutMedicare = filteredExhaustData.filter((p) => {
					if (!p || !p.payerSourceInsurance) return false;
					const payerSourceInsuranceName = p.payerSourceInsurance?.label || p.payerSourceInsurance?.name || '';
					const isPayerSourceMedicare = payerSourceInsuranceName.toLowerCase() === EXHUAST_INSURANCE.MEDICARE.toLowerCase();
					// Exclude Medicare exhaust patients from initial grouping for TOTAL
					return !isPayerSourceMedicare;
				});
				
				// Map remaining exhaust data (non-Medicare) to have insuranceId
				const mappedExhaustData = exhaustWithoutMedicare.map((p) => {
					if (p && p.payerSourceInsurance && (p.payerSourceInsurance._id || p.payerSourceInsurance.id)) {
						return {
							...p,
							insuranceId: p.payerSourceInsurance._id || p.payerSourceInsurance.id,
						};
					}
					return p;
				}).filter((p) => p && p.insuranceId);
				
				// Combine regular data with non-Medicare exhaust data
				combinedData = [...combinedData, ...mappedExhaustData];
				combinedOriginalData = [...combinedOriginalData, ...mappedExhaustData];
			} else {
				// For OUTGOING: Include all exhaust data with Medicare
				const mappedExhaustData = filteredExhaustData.map((p) => {
					if (p && p.payerSourceInsurance && (p.payerSourceInsurance._id || p.payerSourceInsurance.id)) {
						return {
							...p,
							insuranceId: p.payerSourceInsurance._id || p.payerSourceInsurance.id,
						};
					}
					return p;
				}).filter((p) => p && p.insuranceId);
				
				// Combine regular data with exhaust data
				combinedData = [...combinedData, ...mappedExhaustData];
				combinedOriginalData = [...combinedOriginalData, ...mappedExhaustData];
			}
		}
	}
	
	// Note: For INCOMING, exhaust patients are processed separately to show only secondary insurance
	// We don't include them in the initial grouping to avoid creating Medicare entries
	
	// Use combined data length for percentage calculation
	let percentageTotal = totalFilter?.totalForPercentage ? totalFilter.totalForPercentage : (combinedData?.length || exhustListData?.length || 0);

	if (combinedData && combinedData.length > 0) {
		const insuranceDBData = _.groupBy(combinedData, "insuranceId");
		const originalData = _.groupBy(combinedOriginalData, "insuranceId");

		if (insuranceDBData) {
			insuranceDBDataGroup = await getListData(insuranceDBData, "insurance", transferType, percentageTotal, {
				...totalFilter,
				originalData,
			});
		}
	}

	// After main patientData calculation, apply exhaust adjustments
	// Also handle case where there's no regular data but exhaust data exists
	// Skip adjustments when exhaust filter is active (we're already using only exhaust data)
	if (!isExhaustFilterActive && Array.isArray(exhustListData) && exhustListData.length > 0) {
		// Filter exhaust data by transfer type
		let filteredExhaustData = [];
		if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_INCOMING) {
			// For INCOMING: Include all exhaust data (don't filter by isOutgoing)
			// We'll process them to show secondary insurance
			filteredExhaustData = exhustListData;
		} else if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING) {
			filteredExhaustData = _.filter(exhustListData, { isOutgoing: true });
		} else {
			// TOTAL or no transfer type - include ALL exhaust data
			filteredExhaustData = exhustListData;
		}

		// Process exhaust patients based on transfer type
		const exhaustAdjustments = {
			medicareDecrement: 0,
			secondaryIncrement: new Map(), // Map of secondaryInsuranceId -> count
		};

		// Process exhaust patients that have Medicare as payerSourceInsurance
		filteredExhaustData.forEach((p) => {
			if (!p) return;

			const payerSourceInsurance = p.payerSourceInsurance;
			const payerSourceInsuranceName = payerSourceInsurance?.label || payerSourceInsurance?.name || '';
			const isPayerSourceMedicare = payerSourceInsurance && payerSourceInsuranceName.toLowerCase() === EXHUAST_INSURANCE.MEDICARE.toLowerCase();

			if (isPayerSourceMedicare && p.secondaryInsurance && p.secondaryInsurance._id) {
				if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL || !transferType || transferType === "total") {
					// TOTAL: Don't decrement Medicare, but increment Secondary Insurance
					const secondaryId = p.secondaryInsurance._id;
					exhaustAdjustments.secondaryIncrement.set(
						secondaryId,
						(exhaustAdjustments.secondaryIncrement.get(secondaryId) || 0) + 1
					);
				} else if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING) {
					// OUTGOING: Keep as Medicare (main insurance) - no adjustment needed
					// Exhaust patients are already in the main data with Medicare
				} else if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_INCOMING) {
					// INCOMING: Decrement Medicare, increment Secondary Insurance
					exhaustAdjustments.medicareDecrement += 1;
					const secondaryId = p.secondaryInsurance._id;
					exhaustAdjustments.secondaryIncrement.set(
						secondaryId,
						(exhaustAdjustments.secondaryIncrement.get(secondaryId) || 0) + 1
					);
				}
			}
		});

		// Apply adjustments to insuranceDBDataGroup
		if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL || !transferType || transferType === "total") {
			// For TOTAL: Increment Secondary Insurance from exhaust data
			// Do NOT decrement Medicare count for TOTAL transfer type

			// Increment Secondary Insurance counts
			exhaustAdjustments.secondaryIncrement.forEach((count, secondaryId) => {
				const secondaryInsurance = insuranceDBDataGroup.find((item) => item.id === secondaryId || item._id === secondaryId);

				if (secondaryInsurance) {
					// Increment existing Secondary Insurance
					const currentTotal = typeof secondaryInsurance.total === 'string'
						? parseInt(secondaryInsurance.total.replace(/[^0-9-]/g, ''), 10)
						: (secondaryInsurance.total || 0);
					const newTotal = currentTotal + count;
					secondaryInsurance.total = transferType === OVERALL_PAGE_SUB_TYPE.TOTAL || !transferType || transferType === "total"
						? new Intl.NumberFormat("en-US", { signDisplay: "exceptZero" }).format(newTotal)
						: newTotal;
					secondaryInsurance.value = (secondaryInsurance.value || 0) + count;
					secondaryInsurance.originalTotal = (secondaryInsurance.originalTotal || 0) + count;
					// Recalculate percentage
					secondaryInsurance.percentage = itemPercentage(secondaryInsurance.value, percentageTotal, "number");
				} else {
					// Add new Secondary Insurance entry
					const exhaustPatient = filteredExhaustData.find((p) =>
						p.secondaryInsurance && p.secondaryInsurance._id === secondaryId
					);
					if (exhaustPatient && exhaustPatient.secondaryInsurance) {
						const newInsurance = {
							_id: secondaryId,
							id: secondaryId,
							label: exhaustPatient.secondaryInsurance.label || exhaustPatient.secondaryInsurance.name || '',
							name: exhaustPatient.secondaryInsurance.label || exhaustPatient.secondaryInsurance.name || '',
							total: transferType === OVERALL_PAGE_SUB_TYPE.TOTAL || !transferType || transferType === "total"
								? new Intl.NumberFormat("en-US", { signDisplay: "exceptZero" }).format(count)
								: count,
							value: count,
							originalTotal: count,
							isTooltip: totalFilter?.totalType !== TOTAL_TYPE.MAIN,
							isTotalPercentage: transferType === OVERALL_PAGE_SUB_TYPE.TOTAL || !transferType || transferType === "total",
							percentage: itemPercentage(count, percentageTotal, "number"),
						};
						insuranceDBDataGroup.push(newInsurance);
					}
				}
			});

			// Re-sort by total after adjustments
			if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL || !transferType || transferType === "total") {
				insuranceDBDataGroup = _.orderBy(insuranceDBDataGroup, (item) => {
					const total = typeof item.total === 'string'
						? parseInt(item.total.replace(/[^0-9-]/g, ''), 10)
						: (item.total || 0);
					return total;
				}, "desc");
			} else {
				insuranceDBDataGroup = _.orderBy(insuranceDBDataGroup, "total", "desc");
			}
		} else if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_INCOMING) {
			// For INCOMING: Show Secondary Insurance from exhaust data only
			// Medicare should not be shown for INCOMING (exhaust patients switch to secondary insurance)
			
			// Filter exhaust patients that have secondaryInsurance
			const exhaustWithSecondary = filteredExhaustData.filter((p) =>
				p && p.secondaryInsurance && (p.secondaryInsurance._id || p.secondaryInsurance.id)
			);

			if (exhaustWithSecondary.length > 0) {
				// Group exhaust patients by secondaryInsurance
				const exhaustBySecondaryInsurance = _.groupBy(exhaustWithSecondary, (p) => {
					return p.secondaryInsurance._id || p.secondaryInsurance.id;
				});

				for (const insuranceId in exhaustBySecondaryInsurance) {
					const patients = exhaustBySecondaryInsurance[insuranceId];
					if (patients && patients.length > 0 && insuranceId && insuranceId !== 'undefined' && insuranceId !== 'null') {
						const firstPatient = patients[0];
						const insurance = firstPatient.secondaryInsurance;
						if (insurance && (insurance._id || insurance.id)) {
							// Check if this insurance already exists from regular data
							const existingInsurance = insuranceDBDataGroup.find((item) => 
								item.id === insuranceId || item._id === insuranceId
							);
							
							if (existingInsurance) {
								// Increment existing insurance
								existingInsurance.total = (existingInsurance.total || 0) + patients.length;
								existingInsurance.value = (existingInsurance.value || 0) + patients.length;
								existingInsurance.originalTotal = (existingInsurance.originalTotal || 0) + patients.length;
								existingInsurance.percentage = itemPercentage(existingInsurance.value, percentageTotal || exhustListData.length, "number");
							} else {
								// Add new insurance entry
								const insuranceEntry = {
									_id: insurance._id || insurance.id,
									id: insurance._id || insurance.id,
									label: insurance.label || insurance.name || '',
									name: insurance.label || insurance.name || '',
									total: patients.length,
									value: patients.length,
									originalTotal: patients.length,
									isTooltip: totalFilter?.totalType !== TOTAL_TYPE.MAIN,
									isTotalPercentage: false,
									percentage: itemPercentage(patients.length, percentageTotal || exhustListData.length, "number"),
								};
								insuranceDBDataGroup.push(insuranceEntry);
							}
						}
					}
				}
			}

			// For INCOMING: Remove Medicare entries (they should not be shown)
			// Exhaust patients with Medicare switch to secondary insurance for INCOMING
			insuranceDBDataGroup = insuranceDBDataGroup.filter((item) => {
				const insuranceName = item.label || item.name || '';
				const isMedicare = insuranceName.toLowerCase() === EXHUAST_INSURANCE.MEDICARE.toLowerCase();
				// Remove Medicare entries that have 0 or negative values
				if (isMedicare) {
					const totalValue = typeof item.total === 'string'
						? parseInt(item.total.replace(/[^0-9-]/g, ''), 10)
						: (item.total || 0);
					return totalValue > 0;
				}
				return true;
			});
		} else if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING) {
			// For OUTGOING: Exhaust data is already combined with regular data in initial grouping
			// No additional processing needed - exhaust patients are already included with their payerSourceInsurance
			// This section is kept as a fallback in case there's no regular data at all
			if (insuranceDBDataGroup.length === 0 && filteredExhaustData.length > 0) {
				// Fallback: If no data was processed, create entries from exhaust data only
				const exhaustByInsurance = _.groupBy(filteredExhaustData, (p) => {
					return p.payerSourceInsurance?._id || p.payerSourceInsurance?.id;
				});

				for (const insuranceId in exhaustByInsurance) {
					const patients = exhaustByInsurance[insuranceId];
					if (patients && patients.length > 0 && insuranceId && insuranceId !== 'undefined') {
						const firstPatient = patients[0];
						const insurance = firstPatient.payerSourceInsurance;
						if (insurance) {
							const insuranceEntry = {
								_id: insurance._id || insurance.id,
								id: insurance._id || insurance.id,
								label: insurance.label || insurance.name || '',
								name: insurance.label || insurance.name || '',
								total: patients.length,
								value: patients.length,
								originalTotal: patients.length,
								isTooltip: totalFilter?.totalType !== TOTAL_TYPE.MAIN,
								isTotalPercentage: false,
								percentage: itemPercentage(patients.length, percentageTotal || exhustListData.length, "number"),
							};
							insuranceDBDataGroup.push(insuranceEntry);
						}
					}
				}
			}
		}
	}

	return insuranceDBDataGroup;
}


/**
 * Get the exhaustion date for a patient (either from exhaustionDate field or calculated from dateOfADT + medicareDaysRemaining)
 * @param {Object} patient - Patient object
 * @returns {moment.Moment|null} - Exhaustion date as moment object, or null if not available/invalid
 */
export const getMedicareExhaustionDate = (patient) => {
	if (!patient) return null;

	// Prefer exhaustionDate field if available and valid
	if (patient.exhaustionDate) {
		const exhaustionDate = moment(patient.exhaustionDate);
		if (exhaustionDate.isValid()) {
			return exhaustionDate;
		}
	}

	// Fallback: calculate from dateOfADT + medicareDaysRemaining
	if (patient.dateOfADT && patient.medicareDaysRemaining != null) {
		const dateOfADT = moment(patient.dateOfADT);
		if (!dateOfADT.isValid()) return null;
		const daysRemaining =
			typeof patient.medicareDaysRemaining === "string"
				? parseInt(patient.medicareDaysRemaining, 10)
				: patient.medicareDaysRemaining;
		if (isNaN(daysRemaining) || daysRemaining < 0) return null;
		return dateOfADT.clone().add(daysRemaining, "days");
	}

	return null;
};

/**
 * Check if a patient has Medicare insurance and is exhausted
 * @param {Object} patient - Patient object
 * @param {moment.Moment} currentDate - Current date to compare against (defaults to today)
 * @returns {boolean} - True if patient has Medicare and is exhausted
 */
export const isMedicareExhausted = (patient, currentDate = null) => {
	if (!patient) return false;

	const insurance = patient.insurance;
	const insuranceName = insurance?.label || insurance?.name || "";
	if (!insurance || insuranceName.toLowerCase() !== EXHUAST_INSURANCE.MEDICARE.toLowerCase()) {
		return false;
	}

	const exhaustionDate = getMedicareExhaustionDate(patient);
	if (!exhaustionDate) return false;

	const compareDate = currentDate || moment().startOf("day");
	return exhaustionDate.isBefore(compareDate) || exhaustionDate.isSame(compareDate, "day");
};

// Medicare Exhaust card data
// Shows how many patients have exhausted Medicare and the average length (days) from dateOfADT to exhaustionDate.
async function exhaustInsuranceData(data, totalFilter) {
	const { originalData = [], totalType = null, useExhaustListData = false } = totalFilter;
	if (!Array.isArray(data) || data.length === 0) return [];

	const currentDate = moment().startOf("day");

	// If useExhaustListData is true, data/originalData are already pre-filtered
	const exhaustedPatients = useExhaustListData
		? data
		: data.filter((p) => isMedicareExhausted(p, currentDate));
	const exhaustedOriginalPatients = useExhaustListData
		? (Array.isArray(originalData) ? originalData : [])
		: originalData.filter((p) => isMedicareExhausted(p, currentDate));

	const totalExhausted = exhaustedPatients.length;
	const originalTotalExhausted = exhaustedOriginalPatients.length;

	// Average length in days from dateOfADT to exhaustionDate (inclusive counting)
	// Example: dateOfADT = 01/01/2026, exhaustionDate = 01/10/2026 → 10 days (including both dates)
	const calcAvgLength = (patients) => {
		if (!patients.length) return 0;
		let totalDays = 0;
		let count = 0;
		patients.forEach((p) => {
			if (!p.dateOfADT || !p.exhaustionDate) return;
			const start = moment(p.dateOfADT).startOf('day');
			const end = moment(p.exhaustionDate).startOf('day');
			if (!start.isValid() || !end.isValid()) return;
			const diff = end.diff(start, "days");
			if (!Number.isNaN(diff) && diff >= 0) {
				// Add 1 to make it inclusive (including both start and end dates)
				// Example: Jan 1 to Jan 10 = 9 days difference + 1 = 10 days total
				totalDays += (diff + 1);
				count += 1;
			}
		});
		return count ? totalDays / count : 0;
	};

	const averageLength = calcAvgLength(exhaustedPatients);
	const originalAverageLength = calcAvgLength(exhaustedOriginalPatients);

	const percentageBase = totalFilter?.totalForPercentage ?? data.length;

	return [
		{
			_id: OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA,
			id: OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA,
			label: "Medicare Exhaust",
			name: "Medicare Exhaust",
			total: totalExhausted,
			value: totalExhausted,
			originalTotal: originalTotalExhausted,
			isTooltip: totalType !== TOTAL_TYPE.MAIN,
			percentage: itemPercentage(totalExhausted, percentageBase, "number"),
			averageLength,
			originalAverageLength,
			parentCard: PAGE_TYPE.OVERALL,
		},
	];
}

async function doctorData(data, totalFilter) {
	//Doctor list update Cal
	const { transferType = null } = totalFilter;

	let doctorDBDataGroup = [];
	if (data && data.length > 0) {
		const doctorDBData = _.groupBy(data, "doctorId");
		const originalData = _.groupBy(totalFilter.originalData, "doctorId");
		let percentageTotal = totalFilter?.totalForPercentage ? totalFilter.totalForPercentage : data?.length;

		if (doctorDBData) {
			doctorDBDataGroup = await getListData(doctorDBData, "doctor", transferType, percentageTotal, {
				...totalFilter,
				originalData,
			});
		}
	}
	return doctorDBDataGroup;
}

async function floorsData(data, totalFilter) {
	//FloorsData Cal
	const { transferType = null } = totalFilter;

	let unitDBDataGroup = [];
	if (data && data.length > 0) {
		const floorDBData = _.groupBy(data, "floorId");
		const originalData = _.groupBy(totalFilter.originalData, "floorId");
		let percentageTotal = totalFilter?.totalForPercentage ? totalFilter.totalForPercentage : data?.length;

		if (floorDBData) {
			unitDBDataGroup = await getListData(floorDBData, "unit", transferType, percentageTotal, {
				...totalFilter,
				originalData,
			});
		}
	}
	return unitDBDataGroup;
}

export async function daysData(data, totalFilter) {
	let totalData = data;
	const { originalData = [], totalType = null, isOtherDashboard = false, level = 0, transferType = null } = totalFilter;
	const originalDataGroupBy = _.countBy(originalData, "day");
	const dayDBData = _.countBy(totalData, "day");
	let percentageTotal = totalFilter.totalForPercentage ? totalFilter.totalForPercentage : data?.length;
	const patientIdsByDayStatus = _.groupBy(totalData, "day");

	// Handle transferType === OVERALL_PAGE_SUB_TYPE.TOTAL logic similar to floorsData
	if (!transferType || transferType === OVERALL_PAGE_SUB_TYPE.TOTAL) {
		// Group data by day and calculate net transfers for each day
		const dayDBDataGroup = _.groupBy(totalData, "day");
		const originalDataGroup = _.groupBy(originalData, "day");

		// Calculate net transfers for each day
		const dayNetTransfers = {};
		const originalNetTransfers = {};

		for (const dayKey of ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']) {
			const dayPatients = dayDBDataGroup[dayKey] || [];
			const originalDayPatients = originalDataGroup[dayKey] || [];

			dayNetTransfers[dayKey] = await getTotalNumber(dayPatients);
			originalNetTransfers[dayKey] = await getTotalNumber(originalDayPatients);
		}

		const generateDayData = (dayKey) => ({
			_id: dayKey,
			label: dayKey,
			value: dayNetTransfers[dayKey] || 0,
			originalTotal: originalNetTransfers[dayKey] || 0,
			isTooltip: totalType !== TOTAL_TYPE.MAIN,
			color: "#FFECA6",
			percentage: itemPercentage(dayNetTransfers[dayKey] || 0, percentageTotal, "number"),
			patientIds: patientIdsByDayStatus?.[dayKey]?.map(item => isOtherDashboard ? item.refPatientId : item._id) || [],
			level,
			...((isOtherDashboard) && { otherDashboardIds: patientIdsByDayStatus?.[dayKey]?.map(item => item._id) }),
		});

		const daysDataArr = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(generateDayData);
		return daysDataArr;
	} else {
		// Original logic for other transfer types
		const generateDayData = (dayKey) => ({
			_id: dayKey,
			label: dayKey,
			value: dayDBData?.[dayKey] || 0,
			originalTotal: originalDataGroupBy?.[dayKey] || 0,
			isTooltip: totalType !== TOTAL_TYPE.MAIN,
			color: "#FFECA6",
			percentage: itemPercentage(dayDBData?.[dayKey] || 0, percentageTotal, "number"),
			patientIds: patientIdsByDayStatus?.[dayKey]?.map(item => isOtherDashboard ? item.refPatientId : item._id) || [],
			level,
			...((isOtherDashboard) && { otherDashboardIds: patientIdsByDayStatus?.[dayKey]?.map(item => item._id) }),
		});

		const daysDataArr = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(generateDayData);
		return daysDataArr;
	}
}